import { sha256 } from 'js-sha256'
import type { Json } from '../../db_types'
import type { ParsedExecutionResult, PayoutsToInsert, Tier } from '../../types'
import { isTieredPlanState } from '../../types'
import type { AnyTypedNode, ColumnRef, MetricRef, TargetRef, TieredNode } from '../../node/types'
import { isTieredNode } from '../../node/types'
import type { CompensationTierEvent, CompensationTierSnapshotWrite, DolfinRpc } from '../../rpc'

export type TierSnapshotPayload = {
  nodeType: TieredNode['type']
  referenceType: 'target' | 'metric' | 'column'
  reference: Record<string, unknown>
  tiers: Tier[]
  behavior: Record<string, unknown>
}

type TierSnapshotCandidate = Omit<CompensationTierSnapshotWrite, 'payload'> & {
  payload: TierSnapshotPayload
}

type TierReference = {
  referenceType: 'target' | 'metric' | 'column'
  referenceKey: string
  targetId?: number
  reference: Record<string, unknown>
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalize)
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, canonicalize(entry)])
    )
  }

  return value
}

const hashCanonical = (value: unknown) => sha256(JSON.stringify(canonicalize(value)))

const normalizeTarget = (target: TargetRef) => ({
  id: target.id,
  teamTarget: target.team_target,
  companyTarget: target.company_target ?? false,
  globalTarget: target.global_target ?? false,
  weight: target.weight ?? null,
  periodCandidateTargetId: target.period_candidate_target_id ?? null,
  teamIds: ('teams_targets' in target ? (target.teams_targets ?? []) : [])
    .map(team => team.team_id)
    .sort((left, right) => left - right)
})

const getTierReference = (formData: Record<string, unknown>): TierReference | null => {
  const referenceType = formData.reference_type
  if (referenceType === 'target') {
    const targets = (
      Array.isArray(formData.targets) ? formData.targets : formData.target ? [formData.target] : []
    ).filter(
      (target): target is TargetRef =>
        Boolean(target) && typeof target === 'object' && isFiniteNumber((target as TargetRef).id)
    )

    const normalizedTargets = targets.map(normalizeTarget).sort((left, right) => left.id - right.id)
    if (normalizedTargets.length === 0) return null

    return {
      referenceType,
      referenceKey: normalizedTargets.map(target => String(target.id)).join('|'),
      targetId: normalizedTargets.length === 1 ? normalizedTargets[0].id : undefined,
      reference: { targets: normalizedTargets }
    }
  }

  if (referenceType === 'metric') {
    const metric = formData.metric as MetricRef | undefined
    if (!metric || !isFiniteNumber(metric.id)) return null

    return {
      referenceType,
      referenceKey: String(metric.id),
      reference: {
        id: metric.id,
        creditSetupId: metric.credit_setup_id ?? null,
        period: formData.metric_period ?? null
      }
    }
  }

  if (referenceType === 'column') {
    const column = formData.column as ColumnRef | undefined
    if (!column || typeof column.name !== 'string' || column.name.length === 0) return null

    return {
      referenceType,
      referenceKey: column.name,
      reference: { name: column.name }
    }
  }

  return null
}

const getTierConfiguration = (
  node: AnyTypedNode
): {
  reference: TierReference
  payload: TierSnapshotPayload
  hash: string
} | null => {
  if (!isTieredNode(node)) return null

  const formData = node.data.formData as unknown as Record<string, unknown>
  const reference = getTierReference(formData)
  const rawTiers = formData.tiers
  if (!reference || !Array.isArray(rawTiers) || rawTiers.length === 0) return null

  const tiers = rawTiers
    .filter(
      (tier): tier is Tier =>
        Boolean(tier) && typeof tier === 'object' && isFiniteNumber((tier as Tier).attainment)
    )
    .map(tier => ({
      attainment: tier.attainment,
      ...(tier.value !== undefined ? { value: tier.value } : {}),
      ...(tier.percentage !== undefined ? { percentage: tier.percentage } : {}),
      ...(tier.multiplier !== undefined ? { multiplier: tier.multiplier } : {})
    }))
    .sort((left, right) => left.attainment - right.attainment)

  if (
    tiers.length !== rawTiers.length ||
    tiers.some((tier, index) => index > 0 && tier.attainment <= tiers[index - 1].attainment)
  ) {
    return null
  }

  const behavior = Object.fromEntries(
    [
      'type',
      'use_target_weights',
      'cap_target_achievement',
      'full_period_achievement',
      'metric_period',
      'calculate_over_metric',
      'calculate_over_column'
    ]
      .filter(key => key in formData)
      .map(key => [key, canonicalize(formData[key])])
  )

  const payload: TierSnapshotPayload = {
    nodeType: node.type as TierSnapshotPayload['nodeType'],
    referenceType: reference.referenceType,
    reference: reference.reference,
    tiers,
    behavior
  }

  return { reference, payload, hash: hashCanonical(payload) }
}

const getAttainment = (state: { nodeOutput: unknown }): number | undefined => {
  if (!Array.isArray(state.nodeOutput)) return undefined
  const value = state.nodeOutput.find(
    output =>
      output && typeof output === 'object' && 'name' in output && output.name === 'attainment'
  )
  const attainment =
    value && typeof value === 'object' && 'value' in value ? value.value : undefined
  return isFiniteNumber(attainment) ? attainment : undefined
}

const normalizeAttainmentForTierSnapshot = (
  nodeType: TieredNode['type'],
  referenceType: TierSnapshotPayload['referenceType'],
  attainment: number
) =>
  referenceType === 'target' && (nodeType === 'bonus' || nodeType === 'multiplier')
    ? attainment * 100
    : attainment

const getPayoutProfiles = (payouts: PayoutsToInsert[]) =>
  Array.from(
    new Set(
      payouts.flatMap(payout =>
        payout.node_output
          .map(output => output.profile_id)
          .filter((profileId): profileId is string => typeof profileId === 'string')
      )
    )
  )
const getPayoutPeriod = (payouts: PayoutsToInsert[]) => {
  for (const payout of payouts) {
    for (const output of payout.node_output) {
      if (output.period_start && output.period_end) {
        return { start: output.period_start, end: output.period_end }
      }
    }
  }

  return null
}

const getTierIndices = (tiers: Tier[], attainment: number) => {
  let currentTierIndex = -1
  for (const [index, tier] of tiers.entries()) {
    if (attainment < tier.attainment) break
    currentTierIndex = index
  }

  const nextTierIndex = currentTierIndex + 1 < tiers.length ? currentTierIndex + 1 : null
  const currentTier = currentTierIndex >= 0 ? tiers[currentTierIndex] : undefined
  const nextTier = nextTierIndex === null ? undefined : tiers[nextTierIndex]
  const tierSpan = currentTier && nextTier ? nextTier.attainment - currentTier.attainment : null
  const progress =
    currentTier && tierSpan && tierSpan > 0
      ? Math.min(1, Math.max(0, (attainment - currentTier.attainment) / tierSpan))
      : !currentTier && nextTier && nextTier.attainment > 0
        ? Math.min(1, Math.max(0, attainment / nextTier.attainment))
        : null

  return {
    currentTierIndex,
    nextTierIndex,
    isTopTier: currentTierIndex >= 0 && nextTierIndex === null,
    gapToNextTierVsTierSpan:
      currentTier && nextTier && tierSpan && tierSpan > 0
        ? (nextTier.attainment - attainment) / tierSpan
        : !currentTier && nextTier && nextTier.attainment > 0
          ? (nextTier.attainment - attainment) / nextTier.attainment
          : null,
    progressWithinCurrentTierWindow: progress
  }
}

const getPeriod = (result: ParsedExecutionResult) =>
  result.periodStart && result.periodEnd
    ? { start: result.periodStart, end: result.periodEnd }
    : getPayoutPeriod(result.payouts)

export const buildCompensationTierSnapshots = (
  results: ParsedExecutionResult[]
): TierSnapshotCandidate[] => {
  const snapshots = new Map<string, TierSnapshotCandidate>()

  for (const result of results) {
    if (!result.ruleId || !result.ruleVersionId) continue
    const period = getPeriod(result)
    if (!period) continue

    const profileIds = result.profileId ? [result.profileId] : getPayoutProfiles(result.payouts)
    if (profileIds.length === 0) continue

    for (const state of result.path ?? []) {
      if (!isTieredPlanState(state)) continue
      const rawAttainment = getAttainment(state)
      if (rawAttainment === undefined) continue
      const configuration = getTierConfiguration(state.currentNode)
      if (!configuration) continue

      const attainment = normalizeAttainmentForTierSnapshot(
        configuration.payload.nodeType,
        configuration.reference.referenceType,
        rawAttainment
      )
      const indices = getTierIndices(configuration.payload.tiers, attainment)
      for (const profileId of profileIds) {
        const snapshotKey = [
          result.organizationId,
          profileId,
          result.ruleId,
          result.ruleVersionId,
          state.currentNode.id,
          configuration.reference.referenceKey,
          period.start,
          period.end
        ].join(':')

        const candidate: TierSnapshotCandidate = {
          organizationId: result.organizationId,
          profileId,
          compensationRuleId: result.ruleId,
          compensationRuleVersionId: result.ruleVersionId,
          tierNodeId: state.currentNode.id,
          referenceType: configuration.reference.referenceType,
          referenceKey: configuration.reference.referenceKey,
          targetId: configuration.reference.targetId ?? null,
          periodStart: period.start,
          periodEnd: period.end,
          snapshotKey,
          tierConfigHash: configuration.hash,
          payload: configuration.payload,
          attainment,
          currentTierIndex: indices.currentTierIndex,
          nextTierIndex: indices.nextTierIndex,
          isTopTier: indices.isTopTier,
          totalAchievement: attainment,
          totalAchievementPercentage: attainment,
          gapToNextTierVsTierSpan: indices.gapToNextTierVsTierSpan,
          progressWithinCurrentTierWindow: indices.progressWithinCurrentTierWindow
        }
        snapshots.set(snapshotKey, candidate)
      }
    }
  }

  return [...snapshots.values()]
}

export class CompensationTierSnapshots {
  constructor(private readonly rpcClient: DolfinRpc) {}

  async saveForResults(results: ParsedExecutionResult[]): Promise<CompensationTierEvent[]> {
    const snapshots = buildCompensationTierSnapshots(results)
    if (snapshots.length === 0) return []

    const writes: CompensationTierSnapshotWrite[] = snapshots.map(snapshot => ({
      ...snapshot,
      payload: snapshot.payload as unknown as Json
    }))

    return this.rpcClient.saveCompensationTierSnapshots(writes)
  }
}

export default CompensationTierSnapshots
